import { createAsyncThunk } from "@reduxjs/toolkit";
import { logger } from "@amzn/afxtaco-utilities";
import { Project } from "@amzn/afxtaco-types";
import { getProject as getProjectAPI } from "../../src/api/projects/projects";
import {
  setProject,
  invalidateProjectAssets,
  refetchProjectAssets,
} from "../reducers/Projects";
import {
  setCompleted,
  resetExecution,
  CompletionStatus,
} from "../reducers/Execution";
import { reducerType } from "../reducers";

interface HandleExecutionCompletedPayload {
  status: CompletionStatus;
  version?: number;
}

/**
 * Centralized thunk that handles the entire execution-completion lifecycle.
 *
 * The WebSocket handler dispatches this single thunk, which:
 * 1. Sets the COMPLETED status
 * 2. Forces a re-fetch of current assets (covers files that appeared mid-turn,
 *    e.g. a legacy project's lazy migration on a chat-only turn)
 * 3. For asset-generation turns only: fetches updated project metadata and
 *    marks assets as new content (drives the "new content" tab indicators)
 * 4. Resets execution state back to IDLE
 *
 * Components just read derived state — no orchestration logic in effects.
 */
export const handleExecutionCompleted = createAsyncThunk<
  Project | null,
  HandleExecutionCompletedPayload
>("Execution/handleCompleted", async (payload, { dispatch, getState }) => {
  // Step 1: Set COMPLETED status
  dispatch(setCompleted(payload));

  let project: Project | null = null;
  const hasNewAssets = payload.version != null;

  const state = getState() as reducerType;
  const projectId = state.Execution?.projectId ?? null;

  if (payload.status === CompletionStatus.COMPLETED && projectId) {
    // Step 2: Force a re-fetch of current assets on every successful turn.
    // Covers files that appeared mid-turn without being a new logical version
    // (a legacy project's lazy migration on a chat-only turn populates the flat
    // root, but no version is sealed). This bumps the refetch counter only —
    // not the stale counter — so it raises no "new content" indicators.
    dispatch(refetchProjectAssets(projectId));

    // Step 3: For asset-generation turns (backend sent a version number), the
    // agent wrote new content — fetch updated metadata and mark assets new so
    // the tab indicators light up.
    if (hasNewAssets) {
      try {
        const response = await getProjectAPI(projectId);
        if (response.success && response.data) {
          project = response.data;
          dispatch(setProject(response.data));
          dispatch(invalidateProjectAssets(projectId));
        }
      } catch (error) {
        logger.error({
          loggerMsg:
            "[executionThunks] Failed to fetch project after completion",
          projectId,
          error: String(error),
        });
      }
    }
  }

  // Step 4: Reset execution state back to IDLE.
  // This runs after project metadata and asset invalidation are done,
  // so there's no race between components reading completion state.
  dispatch(resetExecution());

  return project;
});
